import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ONE, TWENTY_FIVE, ZERO } from "@/app/constants/constants";
import { sanitizeBrowserQueryParams } from "@/utils/form/queryParams";

export type SortDirection = "asc" | "desc";

interface SortState {
  field?: string;
  direction?: SortDirection;
}

export interface PaginationMeta {
  limit?: number;
  offset?: number;
  currentPage?: number;
  totalPages?: number;
  totalResult?: number;
  sortBy?: string;
  orderBy?: SortDirection;
  search?: string;
}

export interface ResponsePagination {
  limit: number;
  currentPage: number;
  offset: number;
  startIndex: number;
  endIndex: number;
  totalPages: number;
  totalResult: number;
  handleNextClick: () => void;
  handlePreviousClick: () => void;
  handlePageUpdate: (currentPage: number) => void;
  handleLimitUpdate: (limit: number) => void;
  handleOffsetUpdate: (offset: number) => void;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  resetPagination: () => void;
  resetSorting: () => void;
  sortBy?: string;
  orderBy?: SortDirection;
  handleSort: (field: string) => void;
  isSorted: (field: string) => boolean;
  getSortDirection: (field: string) => SortDirection | undefined;
}

export const usePagination = (
  paginationData: PaginationMeta,
  defaultLimit = TWENTY_FIVE,
  setPaginationData?: React.Dispatch<React.SetStateAction<PaginationMeta>>,
  localPagination?: PaginationMeta,
): ResponsePagination => {
  const [searchParams, setSearchParam] = useSearchParams();

  const [limit, setLimit] = useState(defaultLimit);
  const [currentPage, setCurrentPage] = useState(ONE);
  const [offset, setOffset] = useState(ZERO);
  const [totalPages, setTotalPages] = useState(ONE);
  const [totalResult, setTotalResult] = useState(ZERO);

  const [sort, setSort] = useState<SortState>(() => {
    if (localPagination?.sortBy) {
      return {
        field: localPagination.sortBy,
        direction: localPagination.orderBy as SortDirection | undefined,
      };
    }
    return { field: undefined, direction: undefined };
  });

  const patchPaginationData = useCallback(
    (
      patch:
        | Partial<PaginationMeta>
        | ((prev: PaginationMeta) => Partial<PaginationMeta>),
    ): void => {
      if (!setPaginationData) {
        return;
      }

      setPaginationData((previousPagination) => {
        const nextPatch =
          typeof patch === "function" ? patch(previousPagination) : patch;

        return { ...previousPagination, ...nextPatch };
      });
    },
    [setPaginationData],
  );

  const handlePageUpdate = useCallback(
    (nextPage: number): void => {
      if (currentPage === nextPage) {
        return;
      }

      const nextOffset = limit * (nextPage - ONE);

      setCurrentPage(nextPage);
      setOffset(nextOffset);
      patchPaginationData({
        currentPage: nextPage,
        offset: nextOffset,
      });
    },
    [currentPage, limit, patchPaginationData],
  );

  const handleLimitUpdate = useCallback(
    (nextLimit: number): void => {
      if (limit === nextLimit) {
        return;
      }

      setLimit(nextLimit);
      setCurrentPage(ONE);
      setOffset(ZERO);
      patchPaginationData({
        limit: nextLimit,
        currentPage: ONE,
        offset: ZERO,
      });
    },
    [limit, patchPaginationData],
  );

  const handleOffsetUpdate = useCallback(
    (nextOffset: number): void => {
      if (offset === nextOffset) {
        return;
      }

      setOffset(nextOffset);
      patchPaginationData({ offset: nextOffset });
    },
    [offset, patchPaginationData],
  );

  const handleNextClick = useCallback((): void => {
    if (currentPage >= totalPages) {
      return;
    }

    handlePageUpdate(currentPage + ONE);
  }, [currentPage, handlePageUpdate, totalPages]);

  const handlePreviousClick = useCallback((): void => {
    if (currentPage <= ONE) {
      return;
    }

    handlePageUpdate(currentPage - ONE);
  }, [currentPage, handlePageUpdate]);

  const updateSortState = useCallback(
    (newSort: SortState): void => {
      setSort(newSort);

      if (setPaginationData) {
        patchPaginationData({
          orderBy: newSort?.direction,
          sortBy: newSort?.field,
        });
      }
    },
    [patchPaginationData, searchParams, setSearchParam, setPaginationData],
  );

  const getNextSortState = useCallback(
    (field: string): SortState => {
      if (sort.field !== field) {
        return { field, direction: "asc" };
      }

      switch (sort.direction) {
        case "asc":
          return { field, direction: "desc" };
        case "desc":
          return { field: undefined, direction: undefined };
        default:
          return { field, direction: "asc" };
      }
    },
    [sort.direction, sort.field],
  );

  const handleSort = useCallback(
    (field: string): void => {
      const nextSort = getNextSortState(field);
      updateSortState(nextSort);
    },
    [getNextSortState, updateSortState],
  );

  useEffect(() => {
    if (setPaginationData) {
      return;
    }

    setSearchParam(
      (prev) => {
        return sanitizeBrowserQueryParams(prev);
      },
      { replace: true },
    );
  }, [setPaginationData, setSearchParam]);

  const resetSorting = useCallback((): void => {
    updateSortState({ field: undefined, direction: undefined });
  }, [updateSortState]);

  const resetPagination = useCallback((): void => {
    handleLimitUpdate(defaultLimit ?? TWENTY_FIVE);
    resetSorting();
  }, [defaultLimit, handleLimitUpdate, resetSorting]);

  // Tracks whether the hook previously held pagination data
  const prevHadDataRef = useRef(false);

  useEffect(() => {
    if (!paginationData) {
      if (!prevHadDataRef.current) {
        return;
      }

      prevHadDataRef.current = false;

      setLimit(defaultLimit);
      setCurrentPage(ONE);
      setOffset(ZERO);
      setTotalPages(ONE);
      setTotalResult(ZERO);

      return;
    }

    prevHadDataRef.current = true;

    setLimit((previousLimit) => paginationData.limit ?? previousLimit);
    setCurrentPage(
      (previousCurrentPage) =>
        paginationData.currentPage ?? previousCurrentPage,
    );
    setOffset((previousOffset) => paginationData.offset ?? previousOffset);
    setTotalPages(
      (previousTotalPages) => paginationData.totalPages ?? previousTotalPages,
    );
    setTotalResult(
      (previousTotalResult) =>
        paginationData.totalResult ?? previousTotalResult,
    );
  }, [defaultLimit, paginationData]);

  // Sync sort icon state when localPagination changes (e.g. tab switch loads
  // a different persisted sort from IndexedDB). Using `in` so that an explicit
  // undefined value (tab with no active sort) clears any stale sort state.
  useEffect(() => {
    if (localPagination && "sortBy" in localPagination) {
      setSort((prev) => {
        if (
          prev.field === localPagination.sortBy &&
          prev.direction === localPagination.orderBy
        ) {
          return prev;
        }
        return {
          field: localPagination.sortBy,
          direction: localPagination.orderBy as SortDirection | undefined,
        };
      });
    }
  }, [localPagination?.sortBy, localPagination?.orderBy]);

  const isSorted = useCallback(
    (field: string): boolean => sort.field === field,
    [sort.field],
  );

  const getSortDirection = useCallback(
    (field: string): SortDirection | undefined =>
      sort.field === field ? sort.direction : undefined,
    [sort.direction, sort.field],
  );

  return useMemo(() => {
    const startIndex = offset + ONE;
    const endIndex = Math.min(offset + limit, totalResult);
    const hasPreviousPage = currentPage > ONE;
    const hasNextPage = currentPage < totalPages;

    return {
      limit,
      currentPage,
      offset,
      startIndex,
      endIndex,
      totalPages,
      totalResult,
      handleNextClick,
      handlePreviousClick,
      handlePageUpdate,
      handleLimitUpdate,
      handleOffsetUpdate,
      hasPreviousPage,
      hasNextPage,
      resetPagination,
      resetSorting,
      handleSort,
      sortBy: sort.field,
      orderBy: sort.direction,
      isSorted,
      getSortDirection,
    };
  }, [
    currentPage,
    getSortDirection,
    handleLimitUpdate,
    handleNextClick,
    handleOffsetUpdate,
    handlePageUpdate,
    handlePreviousClick,
    handleSort,
    isSorted,
    limit,
    offset,
    resetPagination,
    resetSorting,
    sort.direction,
    sort.field,
    totalPages,
    totalResult,
  ]);
};
